/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    INodeProps,
    Node,
    removeContainerZoomStyle,
    removeInlineZoomStyle,
    IComponentNodeProps,
    IModalViewProps,
    ImagefullView,
    defaultDataScale,
    inlineZoomImageOnHover,
    inlineZoomInitClick,
    inlineZoomImageOnMouseMove,
    inlineZoomImageOnMouseOut
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { Carousel, ICarouselProps } from '@msdyn365-commerce/components';
import { ILobasArticleResources } from '../lobas-article.props.autogenerated';

export interface ILobasImageListProps {
    resources: ILobasArticleResources;
    context: Msdyn365.ICoreContext;
    images?: Msdyn365.IImageData[];
}

/**
 * The interface for IImageListViewProps.
 */
export interface ILobasImageListViewProps {
    imageListContainer: INodeProps;
    imageListItem: INodeProps;
    imageListItems: ILobasImageListItem[] | undefined;
    imageModal: React.ReactElement | null;
}

/**
 * The interface for IImageListItem.
 */
export interface ILobasImageListItem {
    image: JSX.Element | undefined;
}

export interface ILobasImageListNotificationItem {
    key: string;
    value: string;
}

export interface ILobasImageListOnClickNotification {
    notificationItem: ILobasImageListNotificationItem;
}

export interface ILobasImageListState {
    modalIsOpen: boolean;
    activeIndex: number;
    isMobileImageZoomed: boolean;
    isImageZoomed: boolean;
    animating: boolean;
}

/**
 *
 * ImageList component.
 * @extends {React.PureComponent<ILobasImageListProps>}
 */
class ImageList extends React.PureComponent<ILobasImageListProps, ILobasImageListState> {
    private readonly _inlineZoomDivRef: Map<number, HTMLDivElement> = new Map();

    public constructor(props: ILobasImageListProps) {
        super(props);
        this._toggleModal = this._toggleModal.bind(this);
        this.state = { activeIndex: 0, isImageZoomed: false, isMobileImageZoomed: false, modalIsOpen: false, animating: false };

        this._onImageMouseOut = this._onImageMouseOut.bind(this);
        this._inlineZoomImageOnHover = this._inlineZoomImageOnHover.bind(this);
        this._onInlineImageClick = this._onInlineImageClick.bind(this);
    }

    public render(): JSX.Element | null {
        const viewProps: ILobasImageListViewProps = {
            ...this.props,
            imageListContainer: {
                className: classnames('ms-image-list__list', 'lobas-image-list__list'),
                role: 'list',
                tag: 'ul'
            },
            imageListItem: {
                className: classnames('ms-image-list__list-item', 'lobas-image-list__list-item'),
                role: 'listitem',
                tag: 'li',
                onClick: this._openModalDialog
            },
            imageListItems: this._renderImageListItems(),
            imageModal: this.imageModalSlider('')
        };
        return this.imageListView(viewProps);
    }

    /**
     * Render list of images.
     * @returns List of images with text and redirection link.
     */
    private readonly _renderImageListItems = (): ILobasImageListItem[] | undefined => {
        const imageList: Msdyn365.IImageData[] | undefined = this.props.images;
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=100&h=0&q=80&m=6&f=png', w: 100, h: 0 },
                lg: { q: 'w=200&h=0&q=80&m=6&f=png', w: 200, h: 0 },
                xl: { q: 'w=300&h=0&q=80&m=6&f=png', w: 300, h: 0 }
            },
            lazyload: false,
            disableLazyLoad: true
        };
        return imageList?.map((image, index) => {
            const imageProps = {
                gridSettings: this.props.context.request.gridSettings!,
                imageSettings: defaultImageSettings
            };

            // If image is null, we want to render the placeholder SVG - by passing image data with empty src
            const imageImage = (
                <Msdyn365.Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={this.props.context.request} />
            );

            const imageListItem: ILobasImageListItem = {
                image: imageImage
            };
            return imageListItem;
        });
    };

    private imageListView = (props: ILobasImageListViewProps): JSX.Element => {
        const { imageListContainer, imageListItem, imageListItems, imageModal } = props;
        return (
            <>
                <Node {...imageListContainer}>
                    {imageListItems?.map(item => {
                        let indexKey: string = '';

                        if (item.image) {
                            const image = (item.image as unknown) as Msdyn365.Image;
                            indexKey = image.props.src as string;
                        }
                        return (
                            <Node {...imageListItem} key={indexKey}>
                                {item.image && item.image}
                            </Node>
                        );
                    })}
                </Node>
                {imageModal}
            </>
        );
    };

    private readonly _openModalDialog = () => {
        this._toggleModal();
    };

    private _toggleModal(): void {
        if (this.state.modalIsOpen) {
            const parentDiv = this._inlineZoomDivRef.get(this.state.activeIndex);
            if (parentDiv && parentDiv.children && parentDiv.children.length >= 2) {
                const image = parentDiv.children[1].querySelector('img');
                if (image) {
                    image.removeAttribute('style');
                }
            }
            this.setState({
                isImageZoomed: false
            });
        }
        this.setState({
            isImageZoomed: false,
            modalIsOpen: !this.state.modalIsOpen
        });
        removeInlineZoomStyle();
        removeContainerZoomStyle();
    }

    private readonly imageModalSlider = (ZoomView: string): React.ReactElement => {
        const zoomedImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=650&h=0&q=80&m=6&f=png', w: 650, h: 0 },
                lg: { q: 'w=1000&h=0&q=80&m=6&f=png', w: 1000, h: 0 },
                xl: { q: 'w=1600&h=0&q=80&m=6&f=png', w: 1600, h: 0 }
            },
            lazyload: false,
            disableLazyLoad: true
        };
        const carouselprops = {
            tag: Carousel,
            className: classnames('ms-media-gallery__carousel', 'lobas-image-list__carousel'),
            items:
                this.props.images &&
                this.props.images.map((item: Msdyn365.IImageData, index: number) =>
                    this._renderImageInlineOnZoom(item, zoomedImageSettings, index)
                ),
            activeIndex: this.state.activeIndex,
            next: this.next,
            previous: this.previous,
            interval: false,
            directionTextPrev: this.props.resources.previousScreenshotFlipperText,
            directionTextNext: this.props.resources.nextScreenshotFlipperText,
            onIndicatorsClickHandler: this.goToIndex,
            showPaginationTooltip: true,
            hideIndicator: false,
            keyboard: false,
            handleOnExited: this.onExited,
            handleOnExiting: this.onExiting,
            isDisabledFunctionality: this.state.isMobileImageZoomed
        } as IComponentNodeProps<ICarouselProps>;

        const carousel = <Carousel {...carouselprops} />;

        const imageModalSliderProps: IModalViewProps = {
            modalIsOpen: this.state.modalIsOpen,
            ontoggle: this._toggleModal,
            galleryCarousel: carousel,
            classNames: classnames('ms-media-gallery__modal', 'msc-modal-input-required', 'lobas-image-list__modal')
        };
        return ImagefullView(imageModalSliderProps) as React.ReactElement;
    };

    private readonly onExiting = () => {
        this.setState({ animating: true });
    };

    private readonly onExited = () => {
        this.setState({ animating: false });
    };

    /**
     * On click next in carousel.
     */
    private readonly next = (): void => {
        removeInlineZoomStyle();
        if (this.isLastItem() === undefined) {
            return;
        }

        const nextIndex = this.isLastItem() ? 0 : this.state.activeIndex + 1;
        this.goToIndex(nextIndex);

        this.updateZoomedInImage();
    };

    /**
     * On click previous in carousel.
     */
    private readonly previous = (): void => {
        removeInlineZoomStyle();
        const images = this.props.images;
        const nextIndex = this.isFirstItem() ? (images ? images.length - 1 : 0) : this.state.activeIndex - 1;
        this.goToIndex(nextIndex);
        this.updateZoomedInImage();
    };

    private readonly isFirstItem = () => this.state.activeIndex === 0;

    private readonly isLastItem = () => {
        const images = this.props.images;
        return images && this.state.activeIndex === images.length - 1;
    };

    /**
     * Zoomed out image on previous/next click.
     */
    public updateZoomedInImage(): void {
        this.setState({ isImageZoomed: false });
    }

    private readonly goToIndex = (index: number): void => {
        this.setState({ activeIndex: index });
    };

    private readonly _refHandler = (index: number) => (divRef: HTMLDivElement) => {
        this._inlineZoomDivRef.set(index, divRef);
    };

    private _renderImageInlineOnZoom(image: Msdyn365.IImageData, imageSettings: Msdyn365.IImageSettings, index: number): React.ReactNode {
        return (
            <div
                className={classnames(
                    `ms-inline-zoom ${this.state.isImageZoomed ? 'zoomed' : ''}`,
                    `lobas-image-list__inline-zoom ${this.state.isImageZoomed ? 'zoomed' : ''}`
                )}
                ref={this._refHandler(index)}
                data-scale={defaultDataScale}
            >
                <Msdyn365.Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className={classnames('ms-media-gallery__item', 'lobas-image-list__item')}
                    {...image}
                    fallBackSrc={index === 0 ? '' : image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    onClick={this._onInlineImageClick}
                    onMouseOver={(this.state.isImageZoomed && this._inlineZoomImageOnHover) || undefined}
                    id={`lobas-image-list__carousel-item__${index}`}
                    shouldSkipToMainImage
                    imageFallbackOptimize={false}
                    bypassHideOnFailure={index === 0}
                />
                <Msdyn365.Image
                    role='presentation'
                    requestContext={this.props.context.actionContext.requestContext}
                    className={classnames('ms-inline-zoom__zoomedImg', 'lobas-image-list_zoomedImg')}
                    {...image}
                    fallBackSrc={index === 0 ? '' : image.src}
                    onClick={this._onImageMouseOut}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    id={`lobas-image-list__zoom__${index}`}
                    onMouseMove={(this.state.isImageZoomed && inlineZoomImageOnMouseMove) || undefined}
                    shouldSkipToMainImage
                    imageFallbackOptimize={false}
                    bypassHideOnFailure={index === 0}
                />
            </div>
        );
    }

    private _inlineZoomImageOnHover(event: React.MouseEvent<HTMLImageElement>): void {
        inlineZoomImageOnHover(event, String(defaultDataScale));
    }

    private _handleMobileViewZoomedImageClick(event: React.MouseEvent<HTMLImageElement>) {
        const target = event.currentTarget;
        const mobileZoomedInImageClassName = classnames('msc-mobile-zoomed-in', 'lobas-image-list__zoomed-in');
        if (!this.state.isMobileImageZoomed) {
            const bounds = target.getBoundingClientRect();
            const dataScale = Number(defaultDataScale);

            const positionX = event.clientX - bounds.left;
            const positionY = event.clientY - bounds.top;
            const scaledPositionX = positionX * dataScale;
            const scaledPositionY = positionY * dataScale;

            target.style.transform = `scale(${dataScale})`;
            target.classList.add(mobileZoomedInImageClassName);
            target.parentElement!.style.overflow = 'auto';
            target.parentElement!.scrollTo(scaledPositionX - positionX, scaledPositionY - positionY);
            this.setState({
                isMobileImageZoomed: true
            });
        } else {
            target.style.transform = '';
            target.classList.remove(mobileZoomedInImageClassName);
            target.parentElement!.style.overflow = '';
            this.setState({
                isMobileImageZoomed: false
            });
        }
    }

    private _onInlineImageClick(event: React.MouseEvent<HTMLImageElement>): void {
        if (window.innerWidth <= 768) {
            // $msv-breakpoint-m
            this._handleMobileViewZoomedImageClick(event);
            return;
        }
        inlineZoomInitClick(event, String(defaultDataScale));
        this.setState({
            isImageZoomed: true
        });
    }

    private _onImageMouseOut(event: React.MouseEvent<HTMLImageElement, MouseEvent>): void {
        inlineZoomImageOnMouseOut(event);
        this.setState({
            isImageZoomed: false
        });
    }
}

export default ImageList;
